import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Upload, X, Download } from 'lucide-react';
import { toast } from 'sonner';

interface UploadedInventory {
  sku: string;
  quantity: number;
}

interface NervInventoryUploadProps {
  onUpload: (inventory: UploadedInventory[]) => void;
  onClear: () => void;
  uploadedData: UploadedInventory[] | null;
}

const NervInventoryUpload: React.FC<NervInventoryUploadProps> = ({
  onUpload,
  onClear,
  uploadedData,
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const parseCSV = (content: string): UploadedInventory[] => {
    const lines = content.split('\n').filter(line => line.trim());
    
    if (lines.length === 0) {
      throw new Error('CSV file is empty');
    }

    // Parse header
    const header = lines[0].toLowerCase().split(',').map(h => h.trim());
    const skuIndex = header.findIndex(h => h === 'sku');
    const quantityIndex = header.findIndex(h => h === 'quantity' || h === 'qty');

    if (skuIndex === -1 || quantityIndex === -1) {
      throw new Error('CSV must contain "SKU" and "Quantity" columns');
    }

    const inventory: UploadedInventory[] = [];
    const seenSkus = new Set<string>();

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const values = line.split(',').map(v => v.trim());
      const sku = values[skuIndex];
      const quantityStr = values[quantityIndex];

      if (!sku) continue;

      const quantity = parseInt(quantityStr);
      if (isNaN(quantity) || quantity < 0) {
        throw new Error(`Invalid quantity for SKU "${sku}" on line ${i + 1}`);
      }

      // Skip duplicates, keep first occurrence
      if (seenSkus.has(sku.toLowerCase())) {
        continue;
      }
      seenSkus.add(sku.toLowerCase());

      inventory.push({ sku, quantity });
    }

    if (inventory.length === 0) {
      throw new Error('No valid inventory data found in CSV');
    }

    if (inventory.length > 1000) {
      throw new Error('Maximum 1000 SKUs allowed per upload');
    }

    return inventory;
  };

  const handleFileSelect = useCallback(async (file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be under 10MB');
      return;
    }

    try {
      const content = await file.text();
      const inventory = parseCSV(content);
      onUpload(inventory);
      toast.success(`Uploaded ${inventory.length} SKUs successfully`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to parse CSV file');
    }
  }, [onUpload]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const exportUploadedData = useCallback(() => {
    if (!uploadedData || uploadedData.length === 0) return;

    const csv = [
      'SKU,Quantity',
      ...uploadedData.map(item => `${item.sku},${item.quantity}`)
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nerv-uploaded-inventory-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [uploadedData]);

  if (uploadedData) {
    return (
      <Card className="border-primary/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Uploaded Inventory</CardTitle>
              <CardDescription>
                {uploadedData.length} SKUs loaded for what-if analysis
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={exportUploadedData}
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onClear}
              >
                <X className="h-4 w-4 mr-2" />
                Clear
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">
            Total Units: {uploadedData.reduce((sum, item) => sum + item.quantity, 0).toLocaleString()}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>What-If Inventory Analysis</CardTitle>
        <CardDescription>
          Upload a CSV file with SKU and Quantity columns to see projected NERV
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            isDragging
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/50'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-sm text-muted-foreground mb-4">
            Drag and drop your CSV file here, or click to browse
          </p>
          <input
            type="file"
            accept=".csv"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
            }}
            className="hidden"
            id="inventory-upload"
          />
          <label htmlFor="inventory-upload">
            <Button variant="secondary" asChild>
              <span>Choose File</span>
            </Button>
          </label>
          <p className="text-xs text-muted-foreground mt-4">
            CSV Format: SKU, Quantity (max 1000 SKUs, 10MB)
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default NervInventoryUpload;
